import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Upload, Trash2, GripVertical, Play, Square, Download, Loader2, Type,
  Plus, ChevronUp, ChevronDown, Film,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * Client-side social-media reel builder.
 * - Accepts MP4 (any modern browser codec) + GIF uploads.
 * - Chains them into a single canvas stream with per-slide captions.
 * - Uses canvas.captureStream() + MediaRecorder to save an MP4/webm.
 * - Runs entirely in the browser — no server load, no credit burn.
 */

const CAPTION_DEFAULTS = [
  'Casa Samba · New Orleans',
  '$10 per class',
  'First class FREE',
  'Private classes available',
  '📞 504-236-7479',
];

const SLIDE_DURATION_DEFAULT = 3.0; // seconds each slide plays for GIF/still fallback

const uid = () => Math.random().toString(36).slice(2, 9);

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const detectKind = (file) => {
  if (file.type.startsWith('video/')) return 'video';
  if (file.type === 'image/gif') return 'gif';
  if (file.type.startsWith('image/')) return 'image';
  return null;
};

const ReelBuilder = () => {
  const [slides, setSlides] = useState([]);
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0);
  const [outputUrl, setOutputUrl] = useState('');
  const [preparing, setPreparing] = useState(false);

  const canvasRef = useRef(null);
  const abortRef = useRef({ cancelled: false });

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setPreparing(true);
    const captionPool = [...CAPTION_DEFAULTS];
    const next = [];
    for (const file of files) {
      const kind = detectKind(file);
      if (!kind) continue;
      const dataUrl = await readAsDataUrl(file);
      next.push({
        id: uid(),
        kind,
        src: dataUrl,
        name: file.name,
        caption: captionPool.shift() || '',
        duration: kind === 'video' ? null : SLIDE_DURATION_DEFAULT, // null = play full video
      });
    }
    setSlides((prev) => [...prev, ...next]);
    setPreparing(false);
    if (next.length) toast.success(`Added ${next.length} clip${next.length > 1 ? 's' : ''}`);
  };

  const updateSlide = (id, patch) =>
    setSlides((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const removeSlide = (id) => setSlides((prev) => prev.filter((s) => s.id !== id));
  const moveSlide = (id, dir) =>
    setSlides((prev) => {
      const arr = [...prev];
      const idx = arr.findIndex((s) => s.id === id);
      const swap = idx + dir;
      if (idx === -1 || swap < 0 || swap >= arr.length) return prev;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr;
    });

  // Draw a single slide into the canvas until its end condition, then resolve.
  // For videos we drive the video element; for images/gifs we draw once and wait.
  const renderSlide = async (slide, canvas, ctx, onProgress) => {
    const W = canvas.width;
    const H = canvas.height;

    const drawFrame = (media) => {
      // Fill background with black to avoid halos
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);

      // Cover-fit the media into the canvas
      const mw = media.videoWidth || media.naturalWidth || W;
      const mh = media.videoHeight || media.naturalHeight || H;
      const scale = Math.max(W / mw, H / mh);
      const dw = mw * scale;
      const dh = mh * scale;
      const dx = (W - dw) / 2;
      const dy = (H - dh) / 2;
      ctx.drawImage(media, dx, dy, dw, dh);

      // Caption
      if (slide.caption) {
        // Semi-transparent gradient at bottom for readability
        const grad = ctx.createLinearGradient(0, H - 200, 0, H);
        grad.addColorStop(0, 'rgba(0,0,0,0)');
        grad.addColorStop(1, 'rgba(0,0,0,0.7)');
        ctx.fillStyle = grad;
        ctx.fillRect(0, H - 200, W, 200);

        // Text
        ctx.font = '900 62px Georgia, serif';
        ctx.fillStyle = '#f4b400';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'bottom';
        ctx.shadowColor = 'rgba(0,0,0,0.9)';
        ctx.shadowBlur = 12;
        ctx.shadowOffsetY = 4;
        // Wrap simple: break at ~24 chars per line
        const lines = wrapText(ctx, slide.caption, W - 80);
        let y = H - 50;
        for (let i = lines.length - 1; i >= 0; i--) {
          ctx.fillText(lines[i], W / 2, y);
          y -= 70;
        }
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
      }
    };

    if (slide.kind === 'video') {
      const video = document.createElement('video');
      video.src = slide.src;
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      await new Promise((res) => {
        video.onloadedmetadata = () => res();
      });
      await video.play();
      const start = performance.now();
      const totalMs = (video.duration || 3) * 1000;
      await new Promise((resolve) => {
        const tick = () => {
          if (abortRef.current.cancelled) return resolve();
          drawFrame(video);
          const elapsed = performance.now() - start;
          onProgress(Math.min(1, elapsed / totalMs));
          if (video.ended || elapsed >= totalMs) {
            video.pause();
            resolve();
          } else {
            requestAnimationFrame(tick);
          }
        };
        requestAnimationFrame(tick);
      });
    } else {
      // Image / GIF — GIFs animate automatically when drawn from an <img loading="lazy" decoding="async">
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = slide.src;
      await new Promise((res) => {
        img.onload = () => res();
      });
      const start = performance.now();
      const totalMs = (slide.duration || SLIDE_DURATION_DEFAULT) * 1000;
      await new Promise((resolve) => {
        const tick = () => {
          if (abortRef.current.cancelled) return resolve();
          drawFrame(img);
          const elapsed = performance.now() - start;
          onProgress(Math.min(1, elapsed / totalMs));
          if (elapsed >= totalMs) resolve();
          else requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      });
    }
  };

  const wrapText = (ctx, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let current = '';
    for (const w of words) {
      const test = current ? `${current} ${w}` : w;
      if (ctx.measureText(test).width > maxWidth && current) {
        lines.push(current);
        current = w;
      } else {
        current = test;
      }
    }
    if (current) lines.push(current);
    return lines;
  };

  const startRecording = async () => {
    if (slides.length === 0) return toast.error('Add at least one clip first');
    setOutputUrl('');
    setProgress(0);
    setRecording(true);
    abortRef.current.cancelled = false;

    const canvas = canvasRef.current;
    canvas.width = 1080;
    canvas.height = 1920; // 9:16 vertical for TikTok / Instagram Reels
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const stream = canvas.captureStream(30);
    const mimeCandidates = [
      'video/mp4;codecs=avc1',
      'video/webm;codecs=vp9',
      'video/webm;codecs=vp8',
      'video/webm',
    ];
    const mime = mimeCandidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m));
    if (!mime) {
      setRecording(false);
      toast.error('Your browser does not support recording video from canvas.');
      return;
    }
    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
    const chunks = [];
    recorder.ondataavailable = (e) => e.data && chunks.push(e.data);
    const stopped = new Promise((res) => (recorder.onstop = res));
    recorder.start();

    try {
      for (let i = 0; i < slides.length; i++) {
        if (abortRef.current.cancelled) break;
        await renderSlide(slides[i], canvas, ctx, (p) => {
          setProgress(((i + p) / slides.length) * 100);
        });
      }
    } catch (err) {
      console.error(err);
      toast.error('Rendering failed');
    } finally {
      recorder.stop();
      await stopped;
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks, { type: mime });
      const url = URL.createObjectURL(blob);
      setOutputUrl(url);
      setRecording(false);
      setProgress(100);
      if (!abortRef.current.cancelled) toast.success('Reel ready — click Download');
    }
  };

  const stopRecording = () => {
    abortRef.current.cancelled = true;
    toast.info('Stopping...');
  };

  const downloadReel = () => {
    if (!outputUrl) return;
    const a = document.createElement('a');
    a.href = outputUrl;
    const ext = outputUrl.includes('mp4') ? 'mp4' : 'webm';
    a.download = `casa-samba-reel-${Date.now()}.${ext}`;
    a.click();
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <Link to="/admin/dashboard" className="text-white/60 hover:text-yellow-400" data-testid="reel-back">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-black flex items-center gap-2">
                <Film className="w-6 h-6 text-yellow-400" /> Social Reel Builder
              </h1>
              <p className="text-sm text-white/60">
                Upload MP4s or GIFs, add captions, render a vertical reel for Instagram / TikTok.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!recording && (
              <button
                onClick={startRecording}
                disabled={preparing || slides.length === 0}
                data-testid="reel-render-btn"
                className="px-5 py-2.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <Play className="w-4 h-4" /> Render Reel
              </button>
            )}
            {recording && (
              <button
                onClick={stopRecording}
                data-testid="reel-stop-btn"
                className="px-5 py-2.5 rounded-lg bg-red-500 hover:bg-red-400 text-white font-bold text-sm flex items-center gap-2"
              >
                <Square className="w-4 h-4" /> Stop
              </button>
            )}
            {outputUrl && !recording && (
              <button
                onClick={downloadReel}
                data-testid="reel-download-btn"
                className="px-5 py-2.5 rounded-lg bg-green-500 hover:bg-green-400 text-black font-bold text-sm flex items-center gap-2"
              >
                <Download className="w-4 h-4" /> Download
              </button>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          {/* LEFT: canvas preview + slide list */}
          <div className="space-y-4">
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
              <canvas
                ref={canvasRef}
                width={1080}
                height={1920}
                className="w-full max-w-[280px] mx-auto aspect-[9/16] rounded-xl bg-black shadow-xl"
                data-testid="reel-canvas"
              />
              {recording && (
                <div className="mt-3">
                  <div className="text-xs text-white/60 mb-1">Rendering… {progress.toFixed(0)}%</div>
                  <div className="w-full h-2 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-yellow-500 transition-all"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
              {outputUrl && !recording && (
                <video
                  src={outputUrl}
                  controls
                  className="mt-4 w-full max-w-[280px] mx-auto aspect-[9/16] rounded-xl bg-black"
                  data-testid="reel-output-preview"
                />
              )}
            </div>

            <div className="p-4 rounded-2xl bg-white/5 border border-white/10">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold uppercase tracking-wider text-yellow-400">
                  Slides ({slides.length})
                </h3>
                <label
                  htmlFor="reel-upload"
                  className="px-3 py-1.5 rounded-lg bg-yellow-500 hover:bg-yellow-400 text-black text-xs font-bold flex items-center gap-1 cursor-pointer"
                >
                  <Plus className="w-3 h-3" /> Add clips
                </label>
                <input
                  id="reel-upload"
                  type="file"
                  multiple
                  accept="video/*,image/gif,image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => addFiles(e.target.files)}
                  data-testid="reel-upload"
                />
              </div>

              {slides.length === 0 ? (
                <label
                  htmlFor="reel-upload"
                  className="flex flex-col items-center justify-center gap-2 p-8 rounded-xl border-2 border-dashed border-white/20 hover:border-yellow-400 hover:bg-white/5 cursor-pointer text-center transition-colors"
                >
                  <Upload className="w-8 h-8 text-white/60" />
                  <span className="text-sm text-white/80 font-semibold">
                    Drop your MP4 clips or GIFs here
                  </span>
                  <span className="text-xs text-white/40">You can also open your library and pick multiple files</span>
                </label>
              ) : (
                <ul className="space-y-2" data-testid="reel-slide-list">
                  {slides.map((s, idx) => (
                    <li
                      key={s.id}
                      className="flex items-start gap-2 p-2 rounded-xl border border-white/10 bg-black/30"
                      data-testid={`reel-slide-${idx}`}
                    >
                      <GripVertical className="w-4 h-4 text-white/30 mt-2" />
                      <div className="w-16 h-16 rounded-lg bg-black overflow-hidden flex-shrink-0 border border-white/10">
                        {s.kind === 'video' ? (
                          <video src={s.src} muted className="w-full h-full object-cover" />
                        ) : (
                          <img loading="lazy" decoding="async" src={s.src} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0 space-y-1.5">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-white/50">
                          <span className="px-1.5 py-0.5 rounded bg-white/10">{s.kind}</span>
                          <span className="truncate">{s.name}</span>
                        </div>
                        <input
                          type="text"
                          value={s.caption}
                          onChange={(e) => updateSlide(s.id, { caption: e.target.value })}
                          placeholder="Caption (optional)"
                          className="w-full px-2.5 py-1.5 rounded-md bg-white/5 border border-white/10 text-sm"
                          data-testid={`reel-caption-${idx}`}
                        />
                        {s.kind !== 'video' && (
                          <div className="flex items-center gap-2 text-xs">
                            <span className="text-white/50">Duration</span>
                            <input
                              type="number"
                              min="1"
                              max="10"
                              step="0.5"
                              value={s.duration}
                              onChange={(e) => updateSlide(s.id, { duration: Number(e.target.value) || 3 })}
                              className="w-16 px-2 py-0.5 rounded bg-white/5 border border-white/10 text-sm"
                            />
                            <span className="text-white/50">sec</span>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => moveSlide(s.id, -1)}
                          disabled={idx === 0}
                          className="p-1 rounded bg-white/5 hover:bg-white/15 disabled:opacity-30"
                          title="Move up"
                        >
                          <ChevronUp className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => moveSlide(s.id, +1)}
                          disabled={idx === slides.length - 1}
                          className="p-1 rounded bg-white/5 hover:bg-white/15 disabled:opacity-30"
                          title="Move down"
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => removeSlide(s.id)}
                          className="p-1 rounded bg-red-500/10 hover:bg-red-500/30 text-red-400"
                          title="Remove"
                          data-testid={`reel-remove-${idx}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* RIGHT: hints */}
          <div className="space-y-3">
            <div className="p-4 rounded-2xl bg-yellow-500/10 border border-yellow-500/30 text-xs text-yellow-100/85 leading-relaxed">
              <Type className="w-4 h-4 inline-block mr-1 -mt-0.5" />
              <strong className="text-yellow-300">Tips</strong>
              <ul className="mt-2 space-y-1.5 list-disc pl-4">
                <li>Vertical 9:16 output — perfect for Instagram Reels, TikTok, YouTube Shorts.</li>
                <li>MP4 clips play at full length. GIFs / images play for the seconds you set.</li>
                <li>Add captions on each slide — big gold text overlay, safe zone at bottom.</li>
                <li>Everything runs in your browser. No files leave your computer.</li>
                <li>Try to keep total under 60 seconds for social platforms.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReelBuilder;
